import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { defaultIdentifiers } from '@/module/Author/Publication/model/PublicationTypes';

import { useAppNotification } from '@feature/utils';

import { TranslationForm } from './TranslationForm';
import { useTranslationEdit } from './model/TranslationQuery';
import { TranslationSchema, defaultTranslationValues, translationSchema } from './model/TranslationSchema';
import { Translation } from './model/TranslationTypes';

interface TranslationEditProps {
  translation: Translation;
}

const convert = (translation: Translation): TranslationSchema => ({
  ...translation,
  identifiers: translation.identifiers ?? defaultIdentifiers,
});

export function TranslationEdit(props: TranslationEditProps) {
  const { translation } = props;
  const { t } = useTranslation();
  const { mutate, isPending, isError, isSuccess } = useTranslationEdit(translation.id);
  const notification = useAppNotification();

  const onSubmit = (values: TranslationSchema) => {
    mutate(values, {
      onError: notification.error,
      onSuccess: notification.success,
    });
  };

  const values = useMemo(() => convert(translation), [translation]);
  const navigate = useNavigate();

  return (
    <TranslationForm
      title={t('chapters.form.title.edit')}
      schema={translationSchema}
      defaultValues={defaultTranslationValues}
      values={values}
      onSubmit={onSubmit}
      isError={isError}
      isSuccess={isSuccess}
      isSubmitting={isPending}
      onCancel={() => navigate({ to: '..' })}
      showWosFields={false}
      showScopusFields={translation.source.type === 'SCOPUS'}
    />
  );
}
